import { useMutation } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import ReactJson from 'react-json-view';
import usePersistedState from 'shared/hooks/usePersistedState';
import { useAuth } from 'shared/providers/AuthProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ClinicPetParent, InvoiceLineItem, InvoiceLineItemType, StripePaymentIntent } from 'shared/types/graphql';
import { defaultQaLane } from 'shared/utils/http';
import styled from 'styled-components/macro';
import { TRIGGER_CLINIC_SYNC } from '../../graphql/queries';
import { env } from 'env';
import cuid from 'cuid';
import sortBy from 'lodash-es/sortBy';
import { Switch, Flex, Tooltip, Icon, Box, Spinner, Select } from '@televet/kibble-ui';
import { PersistedStateKeys } from 'shared/enums';
import { useTriggerMessageEventAndSubscribe } from 'shared/hooks/useTriggerMessageEventAndSubscribe';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { SqsQueueNames } from '@televet/shared-types/SqsQueueNames';
import { CoreRoutingKey } from '@televet/shared-types/coreJobs/generated/types';
import AutomationsOverView from './AutomationsOverView';
import AutomationVisualizer from './AutomationVisualizer';

const invoiceDate = new Date().toISOString();

const invoiceLineItems = [
  {
    description: 'Abdominal Exploratory',
    amount: 309000,
    code: 'ABDX',
  },
  {
    description: 'Abscess - Minor',
    amount: 2700,
    code: 'ABS1',
  },
  {
    description: 'Flea and Tick Shampoo - 12oz',
    amount: 1765,
    code: 'FTS12',
  },
  {
    description: 'Advantage - Canine 21-55 lbs 6-Pack',
    amount: 9351,
    code: 'ADMC6',
  },
  {
    description: 'Allergy Injection',
    amount: 2100,
    code: 'ALL',
  },
  {
    description: 'Blood Chemistry Panel - Major (16-20)',
    amount: 7900,
    code: 'BPLM',
  },
  {
    description: 'Cavity Repair',
    amount: 11000,
    code: 'CVTY',
  },
  {
    description: 'Cruciate Ligament Repair',
    amount: 61100,
    code: 'CLR',
  },
  {
    description: 'Cyproheptadine 4mg (Inv)',
    amount: 914,
    code: 'CY4I',
  },
  {
    description: 'Exam - Inpatient',
    amount: 2400,
    code: 'EXAM',
  },
  {
    description: 'Fluids IV / liter',
    amount: 2500,
    code: 'IV',
  },
  {
    description: 'Gastronomy',
    amount: 34800,
    code: 'GAS',
  },
  {
    description: 'Liver Panel',
    amount: 7824,
    code: 'LPL',
  },
  {
    description: 'Neuter',
    amount: 16500,
    code: 'NTR',
  },
  {
    description: 'Wound Care and Cleanse',
    amount: 1600,
    code: 'WCC',
  },
  {
    description: 'Sedation',
    amount: 5000,
    code: 'SED',
  },
  {
    description: 'Vetprofen 100mg',
    amount: 900,
    code: 'VP100',
  },
  {
    description: 'Wellness Exam',
    amount: 5000,
    code: 'WELL',
  },
  {
    description: 'Root Canal',
    amount: 11000,
    code: 'RTCN',
  },
  {
    description: 'Tonsillectomy',
    amount: 18800,
    code: 'TONS',
  },
  {
    description: 'Allercept Serum IgE Test Panel',
    amount: 20700,
    code: 'ASIP',
  },
  {
    description: 'Anal Gland(s) Removal',
    amount: 30300,
    code: 'AGRM',
  },
  {
    description: 'Babesiosis Test',
    amount: 9100,
    code: 'BBSS',
  },
  {
    description: 'HeartGard (Bundle)',
    amount: 114696,
    code: 'HRTB',
  },
];

export const generateInvoiceLineItems = (
  amount: number,
): Pick<
  InvoiceLineItem,
  | 'id'
  | 'amount'
  | 'description'
  | 'fee'
  | 'discount'
  | 'type'
  | 'clinicPets'
  | 'provider'
  | 'quantity'
  | 'service'
  | 'connectedCareBenefits'
>[] => {
  const sortedLineItems = sortBy(invoiceLineItems.slice(), ['amount']);
  const minimumLineItemAmount = sortedLineItems[0].amount;

  const lineItems = [];
  let remainingBalance = amount;

  do {
    // pick one of the two highest available amounts to minimize number of line items, but keep it a little varied
    const lineItemIndex = sortedLineItems.length - Math.ceil(Math.random() * 2);
    const lineItem = sortedLineItems[lineItemIndex];
    if (lineItem.amount < remainingBalance) {
      lineItems.push({
        id: cuid(),
        type: InvoiceLineItemType.Code,
        fee: 0,
        discount: 0,
        quantity: 1,
        clinicPets: [],
        connectedCareBenefits: [],
        provider: {
          firstName: 'Dr.',
          lastName: 'Murphy',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        service: {
          code: lineItem.code,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        ...lineItem,
      });
      remainingBalance -= lineItem.amount;
    }
    sortedLineItems.splice(lineItemIndex);
  } while (remainingBalance > minimumLineItemAmount);

  // push wildcard item to account for small remaining balance
  lineItems.push({
    id: cuid(),
    type: InvoiceLineItemType.Code,
    fee: 0,
    discount: 0,
    quantity: 1,
    clinicPets: [],
    connectedCareBenefits: [],
    provider: {
      firstName: 'Dr.',
      lastName: 'Murphy',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    service: {
      code: 'SUPP',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    description: 'Supplies',
    amount: remainingBalance,
  });

  return lineItems;
};

export const defaultDiscoveredInvoiceDemoData = {
  balance: {
    aging30D: 0,
    aging60D: 0,
    aging90D: 0,
    aging120D: 0,
    agingCurrent: -20000,
    currentBalance: -20000,
  },
  invoices: [
    {
      clinicPetParent: {} as ClinicPetParent,
      createdAt: invoiceDate,
      currency: 'usd',
      date: invoiceDate,
      id: 'ckwv8n6vf1019101xq4kku6zh1',
      identifier: '3291991',
      outstandingBalance: 0,
      sentBy: null,
      status: 'Open',
      stripePaymentIntents: [] as StripePaymentIntent[],
      subtotal: 132928,
      taxes: 0,
      total: 132928,
      updatedAt: invoiceDate,
      lineItems: [] as InvoiceLineItem[],
      pimsInvoice: {
        pimsId: '',
      },
    },
  ],
  creditsApplied: 0,
};

const qaList: string[] = [];

for (let qaNumber = 1; qaNumber <= 250; qaNumber++) {
  qaList.push('qa' + qaNumber);
}

enum DiscoveredInvoiceField {
  Invoice = 'Invoice',
  Balance = 'Balance',
}

const DevToolsSettings = (): JSX.Element => {
  const [discoveredInvoiceDemoData, setDiscoveredInvoiceDemoData] = usePersistedState(
    PersistedStateKeys.DiscoveredInvoiceDemoData,
    defaultDiscoveredInvoiceDemoData,
  );
  const [isDiscoveredInvoiceDemoModeEnabled, setIsDiscoveredInvoiceDemoModeEnabled] = usePersistedState(
    PersistedStateKeys.IsDiscoveredInvoiceDemoModeEnabled,
    false,
  );
  const [isAutomationsSenderEnabled, setIsAutomationsSenderEnabled] = usePersistedState(
    PersistedStateKeys.IsAutomationsSenderEnabled,
    false,
  );
  const [isDeveloperModeEnabled, setIsDeveloperModeEnabled] = usePersistedState(
    PersistedStateKeys.IsDeveloperModeEnabled,
    false,
  );
  const [forcedQaLane, setForcedQaLane] = usePersistedState(PersistedStateKeys.ForcedQaLane, '');
  const { clinicUser, currentClinicId } = useClinicUser();
  const { allIntegrations } = useIntegrationsProvider();
  const userId = clinicUser?.id || '';
  const { getToken } = useAuth();
  const [token, setToken] = useState('');
  const [isClinicIdCopied, setIsClinicIdCopied] = useState(false);
  const [isUserIdCopied, setIsUserIdCopied] = useState(false);
  const [isTokenCopied, setIsTokenCopied] = useState(false);

  const [triggerClinicSync, { loading: isTriggerClinicSyncLoading, data: syncData }] = useMutation(TRIGGER_CLINIC_SYNC);
  const { triggerMessageEvent, payload, isTriggerMessageEventLoading } = useTriggerMessageEventAndSubscribe();

  function runTestEvent(): void {
    triggerMessageEvent({
      variables: {
        queueName: SqsQueueNames.CoreQueue,
        event: 'test_success' as CoreRoutingKey,
        data: [{ anything: 'works' }],
        useSubscription: true,
      },
    });
  }
  function runTestErrorEvent(): void {
    triggerMessageEvent({
      variables: {
        queueName: SqsQueueNames.CoreQueue,
        event: 'test_throw_error' as CoreRoutingKey,
        data: [{ errorMessage: 'Custom Error' }],
        useSubscription: true,
      },
    });
  }

  const isProduction = env.REACT_APP_PRODUCTION === 'true';

  useEffect(() => {
    getToken().then((token) => token && setToken(token));
  }, [getToken]);

  const resetCopyState = useCallback(() => {
    setIsClinicIdCopied(false);
    setIsUserIdCopied(false);
    setIsTokenCopied(false);
  }, []);

  const copyToClipboard = useCallback(
    (field: string) => {
      resetCopyState();
      switch (field) {
        case 'clinicId':
          if (currentClinicId) {
            navigator.clipboard.writeText(currentClinicId);
            setIsClinicIdCopied(true);
          }
          break;
        case 'userId':
          navigator.clipboard.writeText(userId);
          setIsUserIdCopied(true);
          break;
        case 'token':
          navigator.clipboard.writeText(token);
          setIsTokenCopied(true);
          break;
      }
      setTimeout(() => {
        resetCopyState();
      }, 3000);
    },
    [currentClinicId, resetCopyState, token, userId],
  );

  const toggleDeveloperMode = useCallback(() => {
    setIsDeveloperModeEnabled((isDeveloperModeEnabled) => !isDeveloperModeEnabled);
  }, [setIsDeveloperModeEnabled]);

  const toggleDiscoveredInvoiceDemoMode = useCallback(() => {
    setIsDiscoveredInvoiceDemoModeEnabled((isDiscoveredInvoiceDemoModeEnabled) => !isDiscoveredInvoiceDemoModeEnabled);
  }, [setIsDiscoveredInvoiceDemoModeEnabled]);

  const toggleAutomationsSender = useCallback(() => {
    setIsAutomationsSenderEnabled((isAutomationsSenderEnabled) => !isAutomationsSenderEnabled);
  }, [setIsAutomationsSenderEnabled]);

  const updateDiscoveredInvoiceDemoData = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, field: DiscoveredInvoiceField) => {
      const value = parseFloat(e.target.value || '0');

      const newData = {
        balance: { ...discoveredInvoiceDemoData.balance },
        invoices: defaultDiscoveredInvoiceDemoData.invoices,
        creditsApplied: defaultDiscoveredInvoiceDemoData.creditsApplied,
      };
      if (field === DiscoveredInvoiceField.Invoice) {
        newData.invoices[0].subtotal = Math.abs(value * 100);
        newData.invoices[0].total = Math.abs(value * 100);
        if (newData.invoices[0].total === 0) {
          newData.invoices = [];
        }
      }
      if (field === DiscoveredInvoiceField.Balance) {
        newData.balance.agingCurrent = value * 100;
        newData.balance.currentBalance = value * 100;
      }

      setDiscoveredInvoiceDemoData(newData);
    },
    [discoveredInvoiceDemoData, setDiscoveredInvoiceDemoData],
  );

  return (
    <Container>
      <ModeSwitch>
        <Switch
          isChecked={isDeveloperModeEnabled}
          onChange={toggleDeveloperMode}
          label="Developer Mode"
          isFieldInline
        />
      </ModeSwitch>

      {!isProduction && (
        <ModeSwitch>
          <Title>
            Invoice Integration Demo Mode
            <Tooltip
              hasArrow
              titleCaseLabel={false}
              label="When turned on, the discovered invoices feature will simulate an account balance (default to a credit of $200) and an invoice (default to $1329.28). When active, use the following input fields to edit those amounts. Payments performed with this invoice will not have  the Invoice ID linked. Reporting will display the relationship to the balance."
            >
              <Box ml={1} as="span" pos="relative">
                <Icon name="infoCircle" size="sm" variant="subtle" />
              </Box>
            </Tooltip>
          </Title>
          <Switch
            isChecked={isDiscoveredInvoiceDemoModeEnabled}
            onChange={toggleDiscoveredInvoiceDemoMode}
            formControlStyle={{ width: 'auto' }}
          />
          {isDiscoveredInvoiceDemoModeEnabled && (
            <>
              <CustomInputArea>
                Balance Amount
                <Tooltip hasArrow titleCaseLabel={false} label="For credit use a negative amount.">
                  <Box ml={1} as="span" pos="relative">
                    <Icon name="infoCircle" size="sm" variant="subtle" />
                  </Box>
                </Tooltip>
                &nbsp;
                <WithDollarSign>
                  $
                  <StyledInput
                    type="number"
                    value={discoveredInvoiceDemoData.balance.currentBalance / 100}
                    onChange={(e): void => updateDiscoveredInvoiceDemoData(e, DiscoveredInvoiceField.Balance)}
                  />
                </WithDollarSign>
              </CustomInputArea>
              <CustomInputArea>
                Invoice Amount
                <Tooltip hasArrow titleCaseLabel={false} label="Invoice amount cannot be negative.">
                  <Box ml={1} as="span" pos="relative">
                    <Icon name="infoCircle" size="sm" variant="subtle" />
                  </Box>
                </Tooltip>
                &nbsp;
                <WithDollarSign>
                  $
                  <StyledInput
                    type="number"
                    min="1"
                    value={(discoveredInvoiceDemoData.invoices?.[0]?.total || 0) / 100}
                    onChange={(e): void => updateDiscoveredInvoiceDemoData(e, DiscoveredInvoiceField.Invoice)}
                  />
                </WithDollarSign>
              </CustomInputArea>
            </>
          )}
        </ModeSwitch>
      )}
      <ModeSwitch>
        <Switch
          isChecked={isAutomationsSenderEnabled}
          onChange={toggleAutomationsSender}
          label="Automations Sender"
          isFieldInline
        />
      </ModeSwitch>
      <AutomationsOverView />
      <AutomationVisualizer />
      <Item>
        <StyledHeader>
          <Title>Clinic Id:</Title>
          <CopyButton isCopied={isClinicIdCopied} onClick={(): void => copyToClipboard('clinicId')}>
            Cop{isClinicIdCopied ? 'ied' : 'y'}
          </CopyButton>
        </StyledHeader>
        <Value>{currentClinicId}</Value>
      </Item>
      <Item>
        <StyledHeader>
          <Title>User Id:</Title>
          <CopyButton isCopied={isUserIdCopied} onClick={(): void => copyToClipboard('userId')}>
            Cop{isUserIdCopied ? 'ied' : 'y'}
          </CopyButton>
        </StyledHeader>
        <Value>{userId}</Value>
      </Item>
      <Item>
        <StyledHeader>
          <Title>User Token:</Title>
          <CopyButton isCopied={isTokenCopied} onClick={(): void => copyToClipboard('token')}>
            Cop{isTokenCopied ? 'ied' : 'y'}
          </CopyButton>
        </StyledHeader>
        <Value>{token && token}</Value>
      </Item>
      {!isProduction && (
        <Item>
          <StyledHeader>
            <Title>Back End Connection:</Title>
          </StyledHeader>
          <Value>
            <Select
              placeholder="Select Back End"
              listProps={{
                isMatchWidthSet: true,
                onSelect: (selectedItem): void => {
                  if (!selectedItem.value) return;
                  if (selectedItem.value === defaultQaLane) {
                    localStorage.removeItem('forcedQaLane');
                  } else {
                    setForcedQaLane(selectedItem.value);
                  }
                  location.reload();
                },
              }}
              maxW="300px"
              value={forcedQaLane || defaultQaLane}
              options={qaList.map((qa) => ({
                value: qa,
                label: `${qa.toLocaleUpperCase()} ${qa === defaultQaLane ? '(Default)' : ''}`,
                isSelected: qa === forcedQaLane,
              }))}
            />
          </Value>
        </Item>
      )}
      <Item>
        <StyledHeader>
          <Title>Integrations:</Title>
        </StyledHeader>
        <Value>
          <ReactJson src={allIntegrations} />
        </Value>
      </Item>
      <Item>
        <StyledHeader>
          <Title>PIMS Sync:</Title>
        </StyledHeader>
        {!clinicUser?.isAdmin ? (
          <Value>User is not an Otto admin</Value>
        ) : !isTriggerClinicSyncLoading ? (
          <SyncButton
            onClick={async (): Promise<void> => {
              triggerClinicSync({
                variables: { clinicId: currentClinicId },
              });
            }}
          >
            Sync Clinic with PIMS
          </SyncButton>
        ) : (
          <>
            <Spinner />
            <br />
          </>
        )}

        <Value>
          <ReactJson src={syncData} />
        </Value>
      </Item>
      <Item>
        <StyledHeader>
          <Title>Job Subscriptions:</Title>
        </StyledHeader>
        <Value>
          <ReactJson src={payload || {}} />
        </Value>
        {!clinicUser?.isAdmin ? (
          <Value>User is not an Otto admin</Value>
        ) : !isTriggerMessageEventLoading ? (
          <Flex>
            <SyncButton
              onClick={async (): Promise<void> => {
                runTestEvent();
              }}
            >
              Trigger Test Job
            </SyncButton>

            <SyncButton
              onClick={async (): Promise<void> => {
                runTestErrorEvent();
              }}
            >
              Trigger Test Error Job
            </SyncButton>
          </Flex>
        ) : (
          <>
            <Spinner />
            <br />
          </>
        )}
      </Item>
    </Container>
  );
};

DevToolsSettings.displayName = 'DevTools';

export default DevToolsSettings;
const Container = styled.div`
  display: flex;
  padding: 20px;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  margin-right: 15px;
`;

const Value = styled.div`
  flex: 1 1 auto;
  margin: 10px 30px;
  font-family: monospace;
  background-color: var(--chakra-colors-background-lessSubtle);
  padding: 10px;
  border-radius: 10px;
  flex-wrap: wrap;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const CopyButton = styled.div<{ isCopied: boolean }>`
  font-size: 12px;
  cursor: pointer;
  padding: 2px 4px;
  border: solid 1px #d3d3d3;
  background-color: ${({ isCopied }): string => (isCopied ? '#41a57d' : '#2ca3de')};
  color: white;
  border-radius: 5px;
`;

const ModeSwitch = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const SyncButton = styled.div`
  cursor: pointer;
  padding: 2px 4px;
  border: solid 1px #d3d3d3;
  background-color: #2ca3de;
  color: white;
  border-radius: 5px;
  width: 200px;
  text-align: center;
  margin: auto;
`;

const CustomInputArea = styled.div`
  margin-left: 20px;
`;

const StyledInput = styled.input`
  padding: 2px 4px;
  text-align: right;
  max-width: 80px;
`;

const WithDollarSign = styled.div`
  display: flex;
  align-items: space-between;
  justify-content: center;
  padding: 2px 4px;
  max-width: 100px;
  background-color: white;
  border: solid 1px #d3d3d3;
  border-radius: 5px;
`;
